import React from "react";

// Where a Database property stands on the open market, read from the Homer data
// the CRM holds (owner_profile.market_*). Christian Gravias, Monday 13198140343
// (17 Oct 2026): show under "Looking to sell" whether the property is for sale
// now or was withdrawn, so agents know which owners to call. The agent's own
// "Looking to sell" text stays as it is; this sits beside it.

const LABELS = {
  for_sale: "For sale",
  under_offer: "Under offer",
  withdrawn: "Withdrawn",
};

const STYLES = {
  for_sale: "bg-[#e9f7ee] text-[#2f8f57]",
  under_offer: "bg-[#fdeede] text-[#c8741f]",
  withdrawn: "bg-[#fdecec] text-[#d14343]",
};

// 2026-03-19 -> "19 Mar 2026", off the string so no timezone shift moves it.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const marketDate = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : "";
};

// "For sale since 12 Sep 2026" / "Withdrawn 19 Mar 2026".
export const marketHeadline = (op = {}) => {
  const label = LABELS[op.market_status];
  if (!label) return "";
  const when = marketDate(op.market_since);
  if (!when) return label;
  return op.market_status === "withdrawn" ? `${label} ${when}` : `${label} since ${when}`;
};

// Compact chip for the grid's "Looking to sell" cell.
export const MarketStatusBadge = ({ op }) => {
  const label = LABELS[op?.market_status];
  if (!label) return null;
  return (
    <span
      title={`Homer: ${[marketHeadline(op), op.market_agency, op.market_price_label].filter(Boolean).join(" · ")}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${STYLES[op.market_status]}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label}
      {op.market_since ? ` ${marketDate(op.market_since).replace(/ \d{4}$/, "")}` : ""}
    </span>
  );
};

// Full reading for the drawer and the Property profile page.
export const MarketStatusLine = ({ op }) => {
  if (!LABELS[op?.market_status]) return null;
  const detail = [op.market_agency, op.market_price_label].filter(Boolean).join(" · ");
  return (
    <div className="mt-1.5 flex flex-col gap-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <MarketStatusBadge op={op} />
        <span className="text-sm text-gray-800">{marketHeadline(op)}</span>
      </div>
      {detail && <div className="text-xs text-gray-500">{detail}</div>}
      <div className="text-[11px] text-gray-400">
        From Homer
        {op.market_url && (
          <>
            {" · "}
            <a href={op.market_url} target="_blank" rel="noopener noreferrer" className="text-[#2f9bd6] hover:underline">
              View listing
            </a>
          </>
        )}
      </div>
    </div>
  );
};

// Grid filter options, sent as ?market=.
export const MARKET_FILTERS = [
  { value: "", label: "Market: any" },
  { value: "any", label: "For sale or withdrawn" },
  { value: "for_sale", label: "For sale" },
  { value: "withdrawn", label: "Withdrawn" },
];
